import { useStableObject } from '@atlassian/post-office-context/use-stable-object';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api/ai-smart-button-store';

import { useEffect } from 'react';
import { useRovoNudgePlacementConfig } from './placement-config';

import type {
	HandleActionType,
	ConversationAction,
} from '@atlassian/rovo-conversation-actions/types';

/**
 * A nudge action that extends `ConversationAction` with additional fields
 * used by the Rovo Smart Button's nudge system.
 *
 * Message makers use these fields to control how their nudge appears and behaves:
 *
 * - `title` — Display text shown in the nudge pill and sidebar
 * - `description` — Supporting text for the nudge
 * - `icon` — A React component factory that returns the icon to show
 * - `handleType` — `HandleActionType.Chat` opens Rovo Chat; `HandleActionType.Base` uses a custom handler
 * - `staticPrompt` — The prompt sent to Rovo Chat when the nudge is clicked (Chat mode)
 * - `starterType` — `'static'` for fixed prompts, `'dynamic'` for context-aware prompts
 * - `nudgeContext` — Contains the `id` used for backend matching in migrated mode
 */
export type RovoNudgeAction = Omit<ConversationAction, 'id' | 'handleType'> & {
	handleType: HandleActionType;
	/** The prompt sent to Rovo Chat when the nudge is clicked (only used with HandleActionType.Chat). */
	staticPrompt?: string;
	/** Whether the prompt is fixed ('static') or generated at runtime ('dynamic'). */
	starterType?: 'static' | 'dynamic';
	/** Backend matching context — the `id` must match a `feature_id` from the convo-ai `/proactive` endpoint. */
	nudgeContext?: {
		id: string;
	};
};

/**
 * Props for the `NudgeDefinition` headless component.
 *
 * Includes all `RovoNudgeAction` fields plus an optional `id` override.
 *
 * The `ConversationAction.id` sent to the Smart Button is a **stable** reporting identifier:
 * `post-office-rovo-nudge`.
 * Dynamic instance data is sent separately on `nudgeId` (`messageTemplateId`).
 */
export type NudgeDefinitionProps = RovoNudgeAction;

/**
 * Headless component used by rovo-nudge message templates to declare how they should appear
 * in the Rovo Smart Button. This component renders nothing to the DOM — instead it calls
 * `setNudges()` directly from the message template render tree.
 *
 * The placement or choreographer selects one active template. Only that template mounts
 * this component and owns the lifecycle of its nudge registration.
 *
 * @example
 * ```tsx
 * import { NudgeDefinition } from '@post-office/browser--post-office-team/rovo-nudge/nudge-definition';
 * import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
 * import SparkleIcon from '@atlaskit/icon/core/sparkle';
 *
 * const MessageFrontend = PlacementFrontend.createMessageFrontend({
 *   render: {
 *     Component: ({ title, description }) => {
 *       const icon = useCallback(() => <SparkleIcon label={title} />, [title]);
 *       return (
 *         <NudgeDefinition
 *           title={title}
 *           description={description}
 *           icon={icon}
 *           handleType={HandleActionType.Chat}
 *           staticPrompt={`Summarize: ${title}`}
 *           starterType="static"
 *           nudgeContext={{ id: 'my-feature-id' }}
 *         />
 *       );
 *     },
 *   },
 * });
 * ```
 */
export function NudgeDefinition(actionProps: NudgeDefinitionProps): null {
	const { setNudges } = useAISmartButtonActions();
	const stableActionProps = useStableObject(actionProps);
	const { nudgeSource } = useRovoNudgePlacementConfig();

	useEffect(() => {
		// `RovoNudgeAction` is a superset of `ConversationAction` with additional nudge-specific
		// fields (for example `staticPrompt`, `starterType`, and `nudgeContext`) that are passed
		// through to the Smart Button API at runtime.
		setNudges([{ id: 'post-office-rovo-nudge', ...stableActionProps }], nudgeSource);

		// A configured source clears independently; the default keeps legacy behavior.
		return () => setNudges([], nudgeSource);
	}, [setNudges, stableActionProps, nudgeSource]);

	return null;
}
